import LegalLayout, { LegalSection, LegalList } from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      testid="legal-privacy-page"
      kicker="Legal"
      title="Privacy Policy"
      lastUpdated="28 February 2026"
      intro="CanniComply is a compliance and training platform. Your team's training records are highly sensitive, so we treat data protection as a first-class product feature — not an afterthought. This policy explains what we collect, why, and how we protect it."
    >
      <LegalSection number="01" title="Who we are" testid="legal-privacy-who">
        <p>
          <b>Data controller:</b> UKCSL Ltd, a company registered in England
          and Wales. UKCSL operates CanniComply in collaboration with Aidoido
          S.L. (Spain), our product-engineering partner. We are jointly
          responsible for the security of your data.
        </p>
        <p>
          You can reach our data-protection team at{" "}
          <a
            href="mailto:privacy@ukcsl.co.uk"
            className="text-white underline underline-offset-4"
          >
            privacy@ukcsl.co.uk
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection number="02" title="What we collect">
        <p>We only collect data that is strictly needed to run the platform:</p>
        <LegalList
          items={[
            "Account data — your name, work email, business name, job title and department.",
            "Compliance content — SOPs and policies you upload, competency assignments, learning modules, question banks and assessment answers.",
            "Certificates — the signed record of every completed acknowledgement or assessment, including scores, timestamps and a unique verification ID.",
            "Usage telemetry — pages visited, actions taken and device/browser type. Used only to keep the service reliable and improve it.",
            "Support correspondence — anything you send us by email.",
          ]}
        />
        <p>
          We do <b>not</b> collect health information, financial information
          beyond what is needed for billing, or any special-category data
          within the meaning of UK GDPR Article 9.
        </p>
      </LegalSection>

      <LegalSection number="03" title="Legal bases">
        <LegalList
          items={[
            "Contract — to provide the platform you're paying for.",
            "Legitimate interest — to keep the service secure, prevent fraud and improve product quality.",
            "Legal obligation — to retain audit-grade compliance records for the period required by regulators.",
            "Consent — for optional marketing communications; you can withdraw this at any time.",
          ]}
        />
      </LegalSection>

      <LegalSection number="04" title="How we store & protect it">
        <LegalList
          items={[
            "Data is stored on UK / EU infrastructure with encryption at rest (AES-256) and in transit (TLS 1.2+).",
            "Access is limited to CanniComply staff with a genuine operational need, and every access is logged.",
            "Certificates carry a tamper-evident verification ID and a QR code that resolves against our public /verify endpoint.",
            "Passwords are never stored in plain text — we use bcrypt with a per-user salt.",
          ]}
        />
      </LegalSection>

      <LegalSection number="05" title="Retention">
        <p>
          Compliance records exist to prove staff training on the day an
          inspector asks. We retain them for the lifetime of your subscription
          plus <b>six years</b> after the last relevant event, which matches
          the retention windows of the primary UK regulators our customers
          answer to (MHRA, Home Office, HSE, ICO).
        </p>
        <p>
          When you close your account we delete or fully anonymise everything
          except the audit trail required by law. You can request a copy of
          your data at any time.
        </p>
      </LegalSection>

      <LegalSection number="06" title="Sharing & sub-processors">
        <p>
          We do not sell your data. We use a small number of vetted
          sub-processors purely to deliver the service:
        </p>
        <LegalList
          items={[
            "Cloud hosting — infrastructure and file storage (UK / EU regions).",
            "Email delivery — Resend, used to send invitations, assignments and reminders.",
            "AI learning generation — Anthropic Claude, called only with the extracted text of SOPs you upload; content is not used to train third-party models.",
            "Product analytics — PostHog, self-hostable and cookie-lite; used to keep the platform reliable.",
          ]}
        />
        <p>
          Each sub-processor is bound by a Data Processing Agreement and is
          reviewed annually. A full list is available on request.
        </p>
      </LegalSection>

      <LegalSection number="07" title="Your rights">
        <p>Under UK GDPR you have the right to:</p>
        <LegalList
          items={[
            "Access the data we hold about you.",
            "Correct data that is inaccurate.",
            "Ask us to delete data we no longer need to hold.",
            "Restrict or object to certain processing.",
            "Take a portable copy of your data to another provider.",
            "Complain to the Information Commissioner's Office (ico.org.uk).",
          ]}
        />
      </LegalSection>

      <LegalSection number="08" title="Cookies">
        <p>
          We use a small number of essential cookies to keep you signed in
          and remember your preferences. See our{" "}
          <a
            href="/legal/cookies"
            className="text-white underline underline-offset-4"
          >
            Cookie Policy
          </a>{" "}
          for details.
        </p>
      </LegalSection>

      <LegalSection number="09" title="Changes to this policy">
        <p>
          If we make a material change we'll notify you by email and give at
          least 30 days' notice before it takes effect.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
